import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { InlineLoadingShimmerComponent } from '../../inline-loading-shimmer/inline-loading-shimmer.component';

@Component({
  selector: 'lib-table-pagination',
  imports: [MatIcon, InlineLoadingShimmerComponent],
  templateUrl: './table-pagination.component.html',
  styleUrl: './table-pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TablePaginationComponent {
  totalCount = input.required<number | null>();
  top = input.required<number | null>();
  noResults = input.required<boolean>();
  readonly loading = input.required<boolean>();
  skip = output<number>();

  paginationButtonsArray = signal<(string | number)[]>([]);
  currentPageIndex = signal(0);

  currentSkip = computed(() => {
    const top = this.top() ?? 0;
    const pageIndex = this.currentPageIndex();
    return pageIndex * top;
  });

  currentPageMin = computed(() => {
    if (this.noResults()) {
      return 0;
    }
    return this.currentSkip() + 1;
  });

  currentPageMax = computed(() => {
    const max = this.currentSkip() + (this.top() ?? 0);
    return Math.min(max, this.totalCount() ?? 0);
  });

  pageCount = computed(() => {
    const top = this.top() ?? 0;
    const totalRows = this.totalCount() ?? 0;

    if (top === 0 || totalRows === 0) {
      return 0;
    }
    return Math.ceil(totalRows / top);
  });

  private listenForTotalRowCountChange = effect(() => {
    this.totalCount();
    this.currentPageIndex.set(0);
  });

  private listenForViewingRowCountChange = toObservable(this.top).subscribe(() => {
    this.currentPageIndex.set(0);
  });

  private generatePageNumbersEffect = effect(() => {
    const pages = this.generatePagination(this.currentPageIndex() + 1, this.pageCount());
    this.paginationButtonsArray.set(pages);
  });

  private skipEmitEffect = effect(() => {
    this.skip.emit(this.currentSkip());
  });

  nextPageClicked() {
    if (this.currentPageIndex() === this.pageCount() - 1 || this.noResults()) {
      return;
    }
    this.currentPageIndex.update(i => i + 1);
  }

  previousPageClicked() {
    if (this.currentPageIndex() === 0 || this.noResults()) {
      return;
    }
    this.currentPageIndex.update(i => i - 1);
  }

  pageClicked(page: number | string) {
    if (page === '...') {
      return;
    }

    const pageNumber = page as number;
    this.currentPageIndex.set(pageNumber - 1);
  }

  private generatePagination(currentPage: number, totalPages: number): (string | number)[] {
    const range: number[] = [];

    if (this.noResults()) {
      return [1];
    }

    // If 7 pages or less, just show all pages
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        range.push(i);
      }
      return range;
    }

    // Add the first page
    range.push(1);

    // Add pages around the current page
    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      if (i > 1 && i < totalPages) {
        range.push(i);
      }
    }

    // Add the last page
    if (totalPages > 1) {
      range.push(totalPages);
    }

    // Sort and add ellipses
    const rangeWithDots = [];
    let lastPage: number | null = null;
    range.sort((a, b) => a - b); // Ensure pages are in order
    let ellipsisCount = 0;

    // First pass to count ellipses
    for (let i = 0; i < range.length - 1; i++) {
      if (range[i + 1] - range[i] > 2) {
        ellipsisCount++;
      }
    }

    // If we have only one ellipsis, ensure the first and last 3 pages are included
    if (ellipsisCount === 1) {
      range.length = 0;
      range.push(1, 2, 3);
      if (currentPage > 3 && currentPage < totalPages - 2) {
        range.push(currentPage);
      }
      range.push(totalPages - 2, totalPages - 1, totalPages);
      range.sort((a, b) => a - b);
    }

    // Add ellipses
    for (const pageNum of range) {
      if (lastPage) {
        if (pageNum - lastPage === 2) {
          rangeWithDots.push(lastPage + 1); // Insert the missing number
        } else if (pageNum - lastPage !== 1) {
          rangeWithDots.push('...'); // Add the ellipsis
        }
      }
      rangeWithDots.push(pageNum);
      lastPage = pageNum;
    }

    return rangeWithDots;
  }
}
